import { Style } from '@/components/style';
import { useEffect, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import PropTypes from 'prop-types';
import { RootStateOrAny, useDispatch, useSelector } from 'react-redux';
import { cloneDeep, isEmpty } from 'lodash';
import { useHistory, useParams } from 'react-router-dom';
import moment from 'moment';
import ServeType from '@/constants/ServeType';
import {
  setWelcomeIsCustomerAddressValid,
  setWelcomeIsCustomerNameValid,
  setWelcomeIsCustomerPhoneValid,
  setWelcomeIsShareMealValid,
  setWelcomePreorderTimeValid,
  setWelcomeSelectedServeTypeValid,
} from '@/actions/customizeWelcome/customizeWelcomeAction';
import * as WelcomeAction from '@actions/welcome/welcomeAction';
import ServeTypeUtils from '@/components/customizeWelcome/utils/serveTypeUtils';
import { setErrorMessage } from '@actions/message/messageAction';
import { setUberDirectApiError } from '@/actions/uberDirect/uberDirectAction';
import * as UberDirectAction from '@/actions/uberDirect/uberDirectAPI';
import UberDirect from '@/utils/uberDirect';
import DeliveryMethodType from '@/constants/DeliveryMethodType';
import formatTWPhone from '@/utils/phoneUtils/formatTWPhone';
import { availableLocales } from '@/constants/locale';
import StoreNotOpenRemind from './components/storeNotOpenRemind';
import WelcomeCTAUtils from './utils/welcomeCTAUtils';

const {
  alertColor,
  blackColor,
  primaryColor,
  gray3Color,
  backgroundColor,
  secondaryColor,
} = new Style().getUiConfig();

type Params = { storeUuid: string };

function WelcomeCTA({ webStoreVo, orderVo }) {
  const style = new Style();
  const history = useHistory();
  const intl = useIntl();
  const { storeUuid } = useParams<Params>();
  const dispatch = useDispatch();
  const [ctaBtnStyle, setCtaBtnStyle] = useState<boolean>(false);
  const lineInfo = useSelector<RootStateOrAny, any>(
    (state) => state.lineReducer.lineProfile
  );
  const webStoreVoResponse = useSelector(
    (state: RootStateOrAny) => state.welcomeReducer.webStoreVoResponse
  );
  const customizeWelcomeReducer = useSelector(
    (state: RootStateOrAny) => state.customizeWelcomeReducer
  );
  const customizedServeType = useSelector<RootStateOrAny, any>(
    (state) => state.modeReducer.customizedServeType
  );
  const deliveryType = useSelector(
    (state: RootStateOrAny) => state.uberDirectReducer.deliveryType
  );
  const selectedCity = useSelector(
    (state: RootStateOrAny) => state.uberDirectReducer.selectedCity
  );
  const selectedArea = useSelector(
    (state: RootStateOrAny) => state.uberDirectReducer.selectedArea
  );
  const selectedStreet = useSelector(
    (state: RootStateOrAny) => state.uberDirectReducer.selectedStreet
  );
  const deliverableAction = useSelector(
    (state: RootStateOrAny) => state.uberDirectReducer.deliverableAction
  );

  const getWebVo = () => {
    const webVo = cloneDeep(webStoreVoResponse.data);

    webVo.lineInfoVo = {
      userId: lineInfo?.userId || null,
    };

    return webVo;
  };

  const setFormInputValidStatus = (isValidFormatData: any) => {
    const {
      isServeTypeValid,
      isPhoneValid,
      isNameValid,
      isAddressValid,
      isPreorderTimeValid,
      isShareMealCondictionValid,
    } = isValidFormatData;

    dispatch(setWelcomeSelectedServeTypeValid(isServeTypeValid));

    dispatch(setWelcomeIsCustomerPhoneValid(isPhoneValid));

    dispatch(setWelcomeIsCustomerNameValid(isNameValid));

    dispatch(setWelcomeIsCustomerAddressValid(isAddressValid));

    dispatch(setWelcomeIsShareMealValid(isShareMealCondictionValid));

    dispatch(setWelcomePreorderTimeValid(isPreorderTimeValid));
  };

  const fixedEncodeURI = (str) => {
    return encodeURI(str);
  };

  const getcustomerAddress = () => {
    if (
      customizeWelcomeReducer?.selectedServeType === 'DELIVERY' &&
      deliveryType
    ) {
      if (
        String(selectedCity) &&
        String(selectedArea) &&
        String(selectedStreet)
      ) {
        const tempStructAddress = String(selectedCity).concat(
          String(selectedArea).concat(String(selectedStreet))
        );
        return tempStructAddress;
      }
    }

    if (customizeWelcomeReducer?.selectedServeType === 'DELIVERY') {
      return customizeWelcomeReducer.customerAddress;
    }
    return '';
  };

  const getStructAddress = () => {
    if (customizeWelcomeReducer?.selectedServeType !== 'DELIVERY') return null;
    if (!deliveryType) return null;
    const tempStructAddress =
      UberDirect.uberDirectStructAddress.createStructAddress(
        String(selectedCity),
        String(selectedArea),
        String(selectedStreet)
      );
    return tempStructAddress;
  };

  const getUberDeliveryMethod = () => {
    if (customizeWelcomeReducer?.selectedServeType !== 'DELIVERY') return null;
    if (!deliveryType) return null;
    if (deliverableAction) {
      return DeliveryMethodType.deliverable_action_meet_at_door;
    }
    return DeliveryMethodType.deliverable_action_leave_at_door;
  };

  const uberDirectAddressValidator = async () => {
    if (customizeWelcomeReducer?.selectedServeType !== 'DELIVERY') return true;
    if (!deliveryType) return true;
    if (!storeUuid) return false;

    if (isEmpty(selectedStreet)) {
      dispatch(setUberDirectApiError('address_empty'));
      return false;
    }

    let result = false;

    const tempAddress = fixedEncodeURI(getStructAddress());

    try {
      const res = await UberDirectAction.uberDeliveryValidate(
        storeUuid,
        String(tempAddress)
      );
      const kind = res?.data?.kind ? cloneDeep(res.data.kind) : null;
      if (kind === 'delivery_quote') {
        result = true;
      } else {
        dispatch(setUberDirectApiError(res));
      }
    } catch (e) {
      console.log('error', e);
    }
    return result;
  };

  const validateOrder = async () => {
    const isValidFormatData = await WelcomeCTAUtils.checkWelcomeCTAValid(
      webStoreVo,
      orderVo,
      customizeWelcomeReducer
    );
    setFormInputValidStatus(isValidFormatData);

    if (!(await uberDirectAddressValidator())) {
      return;
    }

    let isValid = true;

    Object.keys(isValidFormatData).forEach((key) => {
      if (isValidFormatData[key] === false) {
        isValid = false;
      }
    });

    return isValid;
  };

  const submitOrder = async () => {
    const isServeTypeValid = await WelcomeCTAUtils.checkServeTypeValid(
      webStoreVo,
      customizeWelcomeReducer.selectedServeType
    );
    if (
      !isServeTypeValid ||
      (!isInBusinessHours() && !isWelcomeViewMenuText())
    ) {
      setErrorMessage(
        intl.formatMessage(
          { id: 'welcome.store.not.open' },
          {
            serveType: intl.formatMessage({
              id: `welcome.availableServeTypes.${customizeWelcomeReducer.selectedServeType}`,
            }),
          }
        ),
        {
          title: intl.formatMessage({
            id: 'welcome.timeremind.onlineTime.title',
          }),
        }
      );
      return;
    }

    const isValid = await validateOrder();
    if (!isValid && !isWelcomeViewMenuText()) return;
    const newOrderVo = WelcomeCTAUtils.updateNewOrderInfoToOrderVo(
      webStoreVo,
      orderVo,
      customizeWelcomeReducer
    );

    newOrderVo.customerAddress = getcustomerAddress();
    newOrderVo.structuredAddress = getStructAddress();
    newOrderVo.deliverableAction = getUberDeliveryMethod();
    newOrderVo.table.mobile = formatTWPhone(newOrderVo.table.mobile);
    const webVo = getWebVo();
    webVo.orderVo = newOrderVo;

    let tempLocale = 'zh-tw';
    webStoreVo?.langs?.map((lang: any) => {
      Object.keys(availableLocales).map((key) => {
        if (
          availableLocales[key].lang === lang &&
          key === customizeWelcomeReducer.welcomeLocale
        ) {
          tempLocale = lang;
        }
        return false;
      });
      return false;
    });
    webVo.locale = tempLocale.toLowerCase();
    WelcomeAction.post(storeUuid, webVo).then((res: any) => {
      history.push(`${process.env.REACT_APP_VERSION}${res.data.route}`);
    });
  };

  const isInBusinessHours = (): boolean => {
    const { selectedServeType } = customizeWelcomeReducer;
    const { selectedTakeOutServeType } = customizeWelcomeReducer;

    let currentServeType = selectedServeType;
    if (
      selectedServeType === ServeType.TAKE_OUT &&
      selectedTakeOutServeType === ServeType.SCHEDULE_TAKE_OUT
    ) {
      currentServeType = ServeType.SCHEDULE_TAKE_OUT;
    }
    if (
      !isEmpty(
        webStoreVoResponse?.data.webStoreVo?.preorderTimeMap[currentServeType]
          ?.availableTimes
      )
    ) {
      return true;
    }
    const date = new Date();
    const businessHoursCrossDay =
      moment(webStoreVoResponse.data.webStoreVo.endTime, 'HH:mm').diff(
        moment(webStoreVoResponse.data.webStoreVo.startTime, 'HH:mm')
      ) <= 0
        ? 1
        : 0;
    const startTime =
      moment(date).format('YYYY-MM-DDT').toString() +
      webStoreVoResponse.data.webStoreVo.startTime;

    const endTime =
      moment(date)
        .add(businessHoursCrossDay, 'days')
        .format('YYYY-MM-DDT')
        .toString() + webStoreVoResponse.data.webStoreVo.endTime;

    const format = 'YYYY-MM-DDTHH:mm';
    let inBusinessHours = moment(date, format).isBetween(
      moment(startTime, format),
      moment(endTime, format)
    );

    if (!inBusinessHours && businessHoursCrossDay === 1) {
      const midNight = new Date();
      midNight.setHours(0);
      midNight.setHours(0);
      midNight.setSeconds(0);
      const earlyMoringEndTime =
        moment(date).format('YYYY-MM-DDT').toString() +
        webStoreVoResponse.data.webStoreVo.endTime;
      inBusinessHours = moment(date, format).isBetween(
        moment(midNight, format),
        moment(earlyMoringEndTime, format),
        null,
        '[]'
      );
    }

    return inBusinessHours;
  };

  const isViewMenuValid = (): boolean => {
    const date = new Date();
    const menuSchedules = JSON.parse(
      JSON.stringify(webStoreVoResponse.data.webStoreVo.menuSchedules)
    );

    let isMenuView = false;

    for (let i = 0; i < menuSchedules.length; i += 1) {
      // check formdate
      const fromDate = moment(menuSchedules[i].fromDate);
      const toDate = moment(menuSchedules[i].toDate);
      const isDate = moment(date).isBetween(moment(fromDate), moment(toDate));
      if (!isDate) continue;

      // check week
      const dayOfWeek = JSON.parse(JSON.stringify(menuSchedules[i].dayOfWeek));
      const isDayOfWeek = dayOfWeek?.includes(date.getDay());
      if (!isDayOfWeek) continue;

      // check time
      const businessHoursCrossDay =
        moment(menuSchedules[i].dailyToTime, 'HH:mm').diff(
          moment(menuSchedules[i].dailyFromTime, 'HH:mm')
        ) <= 0
          ? 1
          : 0;
      const dailyFromTime =
        moment(date).format('YYYY-MM-DDT').toString() +
        menuSchedules[i].dailyFromTime;
      const dailyToTime =
        moment(date)
          .add(businessHoursCrossDay, 'days')
          .format('YYYY-MM-DDT')
          .toString() + menuSchedules[i].dailyToTime;
      const format = 'YYYY-MM-DDTHH:mm';
      const isDailyTime = moment(date, format).isBetween(
        moment(dailyFromTime, format),
        moment(dailyToTime, format)
      );

      if (isDailyTime) {
        isMenuView = true;
        break;
      }
    }
    return isMenuView;
  };

  // welcome 外送 CTA button 檢查 輸入匡 isValid
  const isDeliveryFormValid = () => {
    if (customizedServeType === 'EMPLOYEE') return true;
  };

  const checkCTABtnStatus = () => {
    const { selectedServeType } = customizeWelcomeReducer;
    const { selectedTakeOutServeType } = customizeWelcomeReducer;
    if (selectedServeType === ServeType.DEFAULT) return;

    if (
      !isInBusinessHours() &&
      ((selectedServeType === ServeType.TAKE_OUT &&
        selectedTakeOutServeType === ServeType.TAKE_OUT) ||
        selectedServeType === ServeType.DINE_IN)
    ) {
      setCtaBtnStyle(false);
      return;
    }

    if (
      selectedServeType === ServeType.TAKE_OUT &&
      selectedTakeOutServeType === ServeType.DEFAULT
    ) {
      setCtaBtnStyle(false);
      return;
    }

    if (selectedServeType === ServeType.DELIVERY && isDeliveryFormValid()) {
      setCtaBtnStyle(true);
      return;
    }

    const isValidFormatData = WelcomeCTAUtils.checkWelcomeCTAStyleStatus(
      webStoreVo,
      orderVo,
      customizeWelcomeReducer
    );

    let isInputFormValid = true;
    Object.keys(isValidFormatData).forEach((key) => {
      if (!isValidFormatData[key]) isInputFormValid = false;
    });

    if (!isInputFormValid) {
      setCtaBtnStyle(false);
      return;
    }

    setCtaBtnStyle(true);
  };

  useEffect(() => {
    if (!webStoreVoResponse?.data || !customizeWelcomeReducer) {
      return;
    }
    checkCTABtnStatus();
  }, [customizeWelcomeReducer]);

  const isWelcomeViewMenuText = () => {
    let isValid = false;

    // 查看菜單顯示條件：
    // 1.Welcome 頁 服務類型 盡快外帶
    // 2.店家營業時間 為 非營業時間
    // 3.菜單顯示時間
    const { selectedServeType } = customizeWelcomeReducer;
    const { selectedTakeOutServeType } = customizeWelcomeReducer;

    if (
      selectedServeType === ServeType.TAKE_OUT &&
      selectedTakeOutServeType === ServeType.TAKE_OUT &&
      !isInBusinessHours() &&
      isViewMenuValid()
    ) {
      isValid = true;
    }
    return isValid;
  };

  const showStoreNotOpenRemind = (): boolean => {
    const { selectedServeType } = customizeWelcomeReducer;
    const { selectedTakeOutServeType } = customizeWelcomeReducer;

    let showFlag = false;

    if (!isInBusinessHours() && selectedServeType === ServeType.DINE_IN) {
      showFlag = true;
    }

    if (
      !isInBusinessHours() &&
      selectedTakeOutServeType === ServeType.TAKE_OUT
    ) {
      showFlag = true;
    }

    return showFlag;
  };

  return (
    <div style={{ width: '100%' }}>
      {showStoreNotOpenRemind() && (
        <StoreNotOpenRemind
          serveType={customizeWelcomeReducer.selectedServeType}
          webStoreVo={webStoreVoResponse.data.webStoreVo}
        />
      )}
      <div
        role="presentation"
        style={{ width: '100%' }}
        onClick={() => submitOrder()}
      >
        {!isEmpty(ServeTypeUtils.checkStoreServeTypeConfig(webStoreVo)) && (
          <div className="btn-group" style={{ width: '100%' }}>
            {isWelcomeViewMenuText() ? (
              <div
                className="btn btn-lg"
                style={style.ctaBtn()}
                data-testid="post_welcome_neworder"
              >
                <FormattedMessage id="welcome.viewMenu" />
              </div>
            ) : (
              <div
                className="btn btn-lg"
                style={!ctaBtnStyle ? style.ctaBtnDisable() : style.ctaBtn()}
                data-testid="post_welcome_neworder"
              >
                <FormattedMessage id="welcome.newOrder" />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

WelcomeCTA.defaultProps = {};

WelcomeCTA.propTypes = {
  webStoreVo: PropTypes.shape({
    supplierWebsite: PropTypes.string,
    supplierName: PropTypes.string,
    displayName: PropTypes.string,
  }).isRequired,
  orderVo: PropTypes.shape({}).isRequired,
};

export default WelcomeCTA;
